import React from 'react'
import {View} from 'react-native'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {useFocusEffect} from '@react-navigation/native'

import {
  type CommonNavigatorParams,
  type NativeStackScreenProps,
} from '#/lib/routes/types'
import {s} from '#/lib/styles'
import {useSetMinimalShellMode} from '#/state/shell'
import {ViewHeader} from '#/view/com/util/ViewHeader'
import {CenteredView} from '#/view/com/util/Views'
import * as SettingsList from '#/screens/Settings/components/SettingsList'
import {atoms as a, useTheme} from '#/alf'
import {Admonition} from '#/components/Admonition'
import {Newspaper_Stroke2_Corner2_Rounded as Newspaper} from '#/components/icons/Newspaper'
import * as Layout from '#/components/Layout'
import {InlineLinkText} from '#/components/Link'
import {Text} from '#/components/Typography'

type Props = NativeStackScreenProps<CommonNavigatorParams, 'Privacy'>
export const PrivacyScreen = (_props: Props) => {
  const {_} = useLingui()
  const t = useTheme()
  const setMinimalShellMode = useSetMinimalShellMode()

  useFocusEffect(
    React.useCallback(() => {
      setMinimalShellMode(false)
    }, [setMinimalShellMode]),
  )

  return (
    <Layout.Screen>
      <ViewHeader title={_(msg`Privacy`)} />
      <CenteredView>
        <View style={[s.p20, a.gap_md]}>
          <Text style={[a.text_md, a.leading_snug, t.atoms.text, a.mb_md]}>
            <Trans>How we built Batesky with your privacy in mind</Trans>
          </Text>

          <Admonition type="tip">
            <Trans>No trackers</Trans>
          </Admonition>

          <Admonition type="tip">
            <Trans>No ads and no ad-cookies</Trans>
          </Admonition>

          <Admonition type="tip">
            <Trans>End-to-end encrypted video calls</Trans>
          </Admonition>

          <Admonition type="tip">
            <Trans>Your profile and posts are public and belong to you</Trans>
          </Admonition>

          <Admonition type="tip">
            <Trans>Batesky Direct messages (DMs) are private</Trans>
          </Admonition>

          <Admonition type="warning">
            <Trans>
              DMs are not end-to-end encrypted (yet). Bluesky DMs are private to
              Batesky, and vice versa.
            </Trans>
          </Admonition>
        </View>

        {/* Guidelines and Policies Section (Settings Style) */}
        <View style={[s.p20]}>
          <Text
            style={[
              {fontSize: 28, marginTop: 20, marginBottom: -30},
              a.font_bold,
              t.atoms.text_contrast_high,
            ]}>
            <Trans />
          </Text>
        </View>
        <View style={[a.pb_2xl]}>
          <SettingsList.Container>
            <SettingsList.LinkItem
              to="/support/privacy-policy"
              label={_(msg`Privacy Policy`)}>
              <SettingsList.ItemIcon icon={Newspaper} />
              <SettingsList.ItemText>
                <Trans>Privacy Policy</Trans>
              </SettingsList.ItemText>
            </SettingsList.LinkItem>
          </SettingsList.Container>

          <Text style={[a.text_md, a.leading_snug, t.atoms.text, a.px_lg]}>
            <Trans>
              For more details please visit our privacy policy. If you have any
              questions please send us a direct message on bsky or email us at
            </Trans>{' '}
            <InlineLinkText
              to="mailto:hello@batesky.app"
              label="hello@batesky.app"
              style={[a.text_md]}>
              hello@batesky.app
            </InlineLinkText>
            <Trans>.</Trans>
          </Text>
        </View>
      </CenteredView>
    </Layout.Screen>
  )
}
